import { el, list, setAttr, setChildren } from 'redom';

import ProfilePictureDisplay from '../../../profile-picture-display';

import deliveredIcon from '../../../assets/images/svg/delivered-icon';
import viewedIcon from '../../../assets/images/svg/viewed-icon';
import moreContentHorizontalIcon from '../../../assets/images/svg/more-content-horizontal-icon';
import copyIcon from '../../../assets/images/svg/copy-icon';
import bookmarkIcon from '../../../assets/images/svg/bookmark-icon';
import unbookmarkIcon from '../../../assets/images/svg/unbookmark-icon';
import feedbackIcon from '../../../assets/images/svg/feedback-icon';
import quoteIcon from '../../../assets/images/svg/quote-icon';

import conversationMessageTypes from '../../../constants/conversation-message-types';

import SenderDisplay from './sender-display';
import RecipientsDisplay from './recipients-display';
import QuotedMessageDisplay from './quoted-message-display';
import MessageAttachment from './attachment';
import EmailAddressNodesCreator from './email-address-nodes-creator';
import GenerationStateDisplay from './generation-state-display';

import {
  parseHRef,
  getClosesAnchor,
  isMessageComplete,
  isStreamingMessage
} from './utilities';

import { EMAIL_TAG } from './message-element';

class MessageDisplay {

  constructor(configuration) {

    this.messagesViewer = configuration.messagesViewer;
    this.serverInterface = configuration.serverInterface;
    this.eventSystem = configuration.eventSystem;
    this.utilities = configuration.utilities;
    this.internalEventCodes = configuration.internalEventCodes;
    this.isInbox = configuration.inbox === true;
    this.displayCopyButton = configuration.displayCopyButton === true;
    this.onMessageCopied = configuration.onMessageCopied || function noop() {};
    this.tools = [];
    this.feedbacksConfiguration = configuration.feedbacks || { canAccept() { return false; }, showForm() {} };
    this.blockPreviewRenderer = configuration.blockPreviewRenderer || null;
    this.blockPreviewDisplay = null;

    this.dateTimeDisplay = null;
    this.deliveredLabel = null;
    this.viewedLabel = null;

    this.senderDisplay = null;
    this.recipientsDisplay = null;
    this.header = null;

    this.message = null;
    this.emailNode = null;

    this.toolsContainer = null;
    this.profilePictureDisplay = new ProfilePictureDisplay({ class: 'small' });
    this.senderPictureContainer = el('.inbox-message-group-message-sender-column', this.profilePictureDisplay);
    this.messageContentDisplay = el('.inbox-message-group-message-content.hide-scrollbar');
    this.generationStateDisplay = new GenerationStateDisplay();
    this.messageAttachmentsDisplay = list('ul.inbox-message-group-message-attachments', MessageAttachment, null, this);
    this.blockPreviewContainer = el('.message-block-preview-container');
    this.blockPreviewContainer.hidden = true;
    this.quotedMessageDisplay = new QuotedMessageDisplay({
      onClick: this.handleQuotedMessageClick.bind(this)
    });
    this.messageBody = el('.main-content', [
      this.renderHeader(),
      this.quotedMessageDisplay,
      this.generationStateDisplay,
      this.messageContentDisplay
    ]);

    this.el = el('li.inbox-message-group-message-main', [
      this.senderPictureContainer,
      el('.inbox-message-group-message-content-column', [
        this.blockPreviewContainer,
        this.messageBody,
        this.messageAttachmentsDisplay,
        this.renderMessageExtrasContainer(),
      ])
    ]);

  }

  renderHeader() {

    this.senderDisplay = new SenderDisplay();
    this.recipientsDisplay = new RecipientsDisplay();

    this.header = el('.header', [
      this.senderDisplay,
      el('.message-recipients-container', [el('label', 'To:\u00A0'),
        this.recipientsDisplay
      ])
    ]);

    return this.header;

  }

  renderMessageMetadataContainer() {

    this.dateTimeDisplay = el('label.inbox-message-date-time', {
      class: 'inbox-message-metadata-label'
    });
    this.deliveredLabel = el('label.inbox-message-delivered-label', {
      class: 'inbox-message-metadata-label'
    }, [deliveredIcon.cloneNode(true)]);
    this.viewedLabel = el('label.inbox-message-viewed-label', {
      class: 'inbox-message-metadata-label'
    }, [viewedIcon.cloneNode(true)]);

    const children = [this.dateTimeDisplay, this.deliveredLabel, this.viewedLabel];

    return el('div.inbox-message-metadata-container', children);

  }

  renderMessageToolsContainer() {

    if (this.displayCopyButton === true) {

      const copyButton = el('button.inbox-message-copy-button', {
        type: 'button',
        onclick: this.copyMessageToClipboard.bind(this)
      }, [copyIcon.cloneNode(true)]);
      this.tools.push(el('x-tooltip', { text: 'Copy' }, [copyButton]));
    }

    return el('div.inbox-message-tools-container', this.tools);

  }

  renderMessageExtrasContainer() {

    return el('div.inbox-message-extras-container', [
      this.renderMessageMetadataContainer(),
      this.toolsContainer = this.renderMessageToolsContainer()
    ]);

  }

  async copyMessageToClipboard() {

    await window.navigator.clipboard.write([
      // eslint-disable-next-line no-undef
      new ClipboardItem({
        'text/html': new Blob([this.message.serializedContent.html], { type: 'text/html' })
      })
    ]);
    this.onMessageCopied();

  }

  updateDeliveryStatLabel(deliveredLabelStatus, viewedLabelStatus, value) {

    const deliveredLabelAttributes = {
      title: this.utilities.dateTimeFormatter(value),
      style: {
        display: deliveredLabelStatus
      }
    };

    const viewedLabelAttributes = {
      title: this.utilities.dateTimeFormatter(value),
      style: {
        display: viewedLabelStatus
      }
    };

    setAttr(this.deliveredLabel, deliveredLabelAttributes);
    setAttr(this.viewedLabel, viewedLabelAttributes);

  }

  updateMetadata(message) {

    this.dateTimeDisplay.textContent = this.utilities.dateTimeFormatter(message.updatedAt || message.publishedAt);

    if (message.viewedAt !== null) {
      this.updateDeliveryStatLabel('none', 'inline-block', message.viewedAt);
    } else if (message.deliveredAt !== null) {
      this.updateDeliveryStatLabel('inline-block', 'none', message.deliveredAt);
    }

  }

  tryDefineEmailTag(message, html) {

    const elementTag = `v-${message._id}`;

    if (customElements.get(elementTag) !== void 0) {
      return elementTag;
    }

    const eventSystem = this.eventSystem;
    const internalEventCodes = this.internalEventCodes || {};

    customElements.define(elementTag,
      class extends HTMLElement {
        constructor() {
          super();

          this.minimized = true;
          this.emailQuoteElements = [];
          this.minimizedToggleButton = null;
          this.div = el('div', {
            style: {
              'overflow-wrap': 'anywhere'
            },
            onclick: this.handleClick.bind(this)
          });
          this.div.innerHTML = html;
          const shadowRoot = this.attachShadow({ mode: 'open' });
          shadowRoot.appendChild(this.div);
          shadowRoot.appendChild(el('style', {
            type: 'text/css'
          }, `
            div, html {
              height: auto;
            }

            .minimized-toggle-button {

                width: 30px;
                height: 15px;
                box-sizing: border-box;
                border: unset;
                background: unset;
                cursor: pointer;
                border-radius: var(--application-border-radius);
                border: 1px solid transparent;
                transition: border-color var(--application-transition-duration) var(--application-transition-function);
                padding: 0;

            }

            .minimized-toggle-button:hover {

               border-color: var(--startup-tertiary-color);

            }

              .minimized-toggle-button svg {
                width: 100%;
                height: 100%;
              }

             .minimized-toggle-button svg path {
                stroke: var(--startup-tertiary-color);
                transition: stroke var(--application-transition-duration) var(--application-transition-function);
              }

            .minimized-toggle-button:hover  path{

               stroke: var(--startup-tertiary-color);

            }

          `));

          this.tryMinimizeEmailQuote();
        }

        hideEmailQuote() {

          this.emailQuoteElements.forEach(emailQuoteElement => {

            setAttr(emailQuoteElement, {
              style: {
                display: 'none'
              }
            });

          });

        }

        showEmailQuote() {

          this.emailQuoteElements.forEach(emailQuoteElement => {

            setAttr(emailQuoteElement, {
              style: {
                display: 'block'
              }
            });

          });

        }

        toggleMinimized(event) {

          event.preventDefault();
          event.stopPropagation();

          if (this.minimized === true) {
            this.minimized = false;
            this.showEmailQuote();
          } else {
            this.minimized = true;
            this.hideEmailQuote();
          }

        }

        addMinimizedToggleButtonForPlatform(emailQuote) {

          this.emailQuoteElements = Array.from(emailQuote.children);

          this.hideEmailQuote();

          this.minimizedToggleButton = el('button.minimized-toggle-button', {
            type: 'button',
            onclick: this.toggleMinimized.bind(this)
          }, [moreContentHorizontalIcon.cloneNode(true)]);

          emailQuote.prepend(this.minimizedToggleButton);

        }

        addMinimizedToggleButtonForGmail(emailQuote) {

          this.addMinimizedToggleButtonForPlatform(emailQuote);

        }

        addMinimizedToggleButtonForYahoo(emailQuote) {

          this.addMinimizedToggleButtonForPlatform(emailQuote);

        }

        addMinimizedToggleButtonForMicrosoft(separtor) {

          const appendedContent = [];

          while (separtor?.nextElementSibling !== null) {
            appendedContent.push(separtor.nextElementSibling);
            separtor.nextElementSibling.remove();
          }

          const container = el('.ms-email-appended-content', appendedContent);

          separtor.after(container);

          this.emailQuoteElements = appendedContent;

          this.hideEmailQuote();

          const minimizedToggleButton = el('button.minimized-toggle-button', {
            type: 'button',
            onclick: this.toggleMinimized.bind(this)
          }, [moreContentHorizontalIcon.cloneNode(true)]);
          container.prepend(minimizedToggleButton);

        }

        addMinimizedToggleButtonForOutlook(separtor) {

          const lastElement = separtor.previousElementSibling;

          if (lastElement === null) {
            return;
          }

          this.addMinimizedToggleButtonForMicrosoft(lastElement);

        }

        isVenturzEmail() {

          return this.div.querySelector(':scope >.editor-element-wrapper') !== null;

        }

        removeMinimizedToggleButton() {

          if (this.minimizedToggleButton === null) {
            return;
          }

          this.minimizedToggleButton.parentNode.removeChild(this.minimizedToggleButton);

        }

        tryMinimizeEmailQuote() {

          const emailQuote = this.div.querySelector('.email-quote');

          if (this.isVenturzEmail() === true && emailQuote !== null) {
            this.addMinimizedToggleButtonForPlatform(emailQuote);
            return;
          }

          const gmailQuote = this.div.querySelector('div.gmail_quote');

          if (gmailQuote !== null) {
            this.addMinimizedToggleButtonForGmail(gmailQuote);
            return;
          }

          const yahooQuote = this.div.querySelector('div.yahoo_quoted');

          if (yahooQuote !== null) {
            this.addMinimizedToggleButtonForYahoo(yahooQuote);
            return;

          }

          const microsoftQuote = this.div.querySelector('div#appendonsend');

          if (microsoftQuote !== null) {
            this.addMinimizedToggleButtonForMicrosoft(microsoftQuote);
            return;
          }

          const outlookQuote = this.div.querySelector('div.OutlookMessageHeader');

          if (outlookQuote !== null) {
            this.addMinimizedToggleButtonForOutlook(outlookQuote);
            return;
          }

          const mozCitePrefix = this.div.querySelector('div.moz-cite-prefix');

          if (mozCitePrefix !== null) {
            this.addMinimizedToggleButtonForOutlook(mozCitePrefix);
            return;
          }

          const appleQuote = this.div.querySelector('blockquote[type=cite]');

          if (appleQuote !== null) {
            this.addMinimizedToggleButtonForPlatform(appleQuote);
            return;
          }

          const defaultQuote = this.div.querySelector(':scope > blockquote');

          if (defaultQuote !== null) {
            this.addMinimizedToggleButtonForOutlook(defaultQuote);
            return;
          }

        }

        handleClick(event) {

          const element = event.target;
          const anchor = getClosesAnchor(element);

          if (anchor === null) {
            return;
          }

          event.preventDefault();

          const href = anchor.getAttribute('href');
          const result = parseHRef(href);
          const type = result.type;

          if (type === 'email-link') {
            eventSystem.internal.emit(internalEventCodes.EMAIL_LINK_CLICKED, {
              emailAddress: result.value
            });
          } else if (type !== 'empty') {
            window.open(href, '_blank');
          }

        }
      }
    );

    return elementTag;

  }

  createEmailNode() {

    var eventSystem = this.eventSystem;
    var internalEventCodes = this.internalEventCodes || {};

    var emailNode = el(EMAIL_TAG);
    emailNode.onEmailClick = function onEmailClick(data) {
      eventSystem.internal.emit(internalEventCodes.EMAIL_LINK_CLICKED, data);
    };

    this.emailNode = emailNode;

    return emailNode;

  }

  getOrCreateEmailNode() {

    if (this.emailNode !== null) {
      return this.emailNode;
    }

    return this.createEmailNode();

  }

  setContent(message, html) {

    var emailNode = this.createEmailNode();

    emailNode.contentHTML = html;
    setChildren(this.messageContentDisplay, [emailNode]);

  }

  streamContent(html) {

    var emailNode = this.getOrCreateEmailNode();

    emailNode.contentHTML = html;

  }

  async updateContent(message) {

    if (this.messageContentDisplay.displayed === true) {
      if (isMessageComplete(message) === false && isStreamingMessage(message) === false) {
        return;
      }
    }

    var html = message.serializedContent.html;

    if (typeof message.channelMetadata.rawEmailHTMLId === 'string' && message.serializedContent.loaded !== true) {
      this.messageContentDisplay.innerHTML = '<ht-spinner />';
      message.serializedContent.html = await this.serverInterface.getEmailConversationMessageById(message.conversationId, message._id);
      message.serializedContent.loaded = true;
      html = message.serializedContent.html;
    }

    if (isStreamingMessage(message) === true && this.messageContentDisplay.displayed === true) {
      this.streamContent(html);
    } else {
      this.setContent(message, html);
    }

    var emailAddressNodesCreator = new EmailAddressNodesCreator(this.messageContentDisplay);
    emailAddressNodesCreator.create();

    this.messageContentDisplay.displayed = true;
  }

  updateAttachments(message) {

    this.messageAttachmentsDisplay.update(message.attachments);

  }

  updateBlockPreview(message) {

    if (this.blockPreviewRenderer === null) {
      return;
    }

    var metadata = message.metadata;
    var selectedBlocks = null;

    if (metadata && metadata.activeContext && metadata.activeContext.viewedData) {
      selectedBlocks = metadata.activeContext.viewedData.selectedBlocks;
    }

    if (!selectedBlocks || selectedBlocks.length === 0) {
      this.blockPreviewContainer.hidden = true;
      return;
    }

    if (this.blockPreviewDisplay === null) {
      this.blockPreviewDisplay = this.blockPreviewRenderer();
      setChildren(this.blockPreviewContainer, [this.blockPreviewDisplay.el]);
    }

    this.blockPreviewDisplay.previewSelection(selectedBlocks);
    this.blockPreviewContainer.hidden = false;

  }

  updateQuotedMessage(message) {

    const quotedMessageId = message.quotedMessageId;

    if (typeof quotedMessageId !== 'string') {
      this.quotedMessageDisplay.update(null);
      return;
    }

    this.quotedMessageDisplay.update(this.messagesViewer.getQuotedMessageById(quotedMessageId));

  }

  handleQuotedMessageClick() {

    this.messagesViewer.scrollToMessage(this.message.quotedMessageId);

  }

  getSampleRecipients() {

    const recipients = [{
      _id: 'sample-id',
      fromName: 'Sample name',
      fromEmailAddress: 'sample@sample.com'
    }, {
      _id: 'sample-id',
      fromName: 'Sample name',
      fromEmailAddress: 'sample@sample.com'
    }, {
      _id: 'sample-id',
      fromName: 'Sample name',
      fromEmailAddress: 'sample@sample.com'
    }, {
      _id: 'sample-id',
      fromName: 'Sample name',
      fromEmailAddress: 'sample@sample.com'
    }];

    return recipients;

  }

  showHeader(message, sender) {

    setAttr(this.header, {
      style: {
        display: 'flex'
      }
    });

    this.senderDisplay.update(message.sender);
    this.senderDisplay.textContent = sender.displayName;
    this.recipientsDisplay.update(message.allReceivers || []);

  }

  hideHeader() {

    setAttr(this.header, {
      style: {
        display: 'none'
      }
    });

  }

  async handleBookmarkButtonClick(event) {

    try {

      event.preventDefault();
      event.stopPropagation();

      setAttr(event.target, {
        disabled: true
      });

      const messageId = this.message._id;

      await this.messagesViewer.bookmark(messageId, this);

    } catch (exception) {

      // eslint-disable-next-line no-console
      console.error(exception);

    } finally {
      setAttr(event.target, {
        disabled: false
      });
    }

  }

  async handleUnbookmarkButtonClick(event) {

    try {

      event.preventDefault();
      event.stopPropagation();

      setAttr(event.target, {
        disabled: true
      });

      const messageId = this.message._id;

      await this.messagesViewer.unbookmark(messageId, this);

    } catch (exception) {

      // eslint-disable-next-line no-console
      console.error(exception);

    } finally {
      setAttr(event.target, {
        disabled: false
      });
    }

  }

  async handleQuoteButtonClick(event) {

    try {

      event.preventDefault();
      event.stopPropagation();

      setAttr(event.target, {
        disabled: true
      });

      await this.messagesViewer.quote(this.message);

    } catch (exception) {

      // eslint-disable-next-line no-console
      console.error(exception);

    } finally {
      setAttr(event.target, {
        disabled: false
      });
    }

  }

  tryRenderQuoteTool(tools) {

    if (this.messagesViewer.canQuoteMessages() === false) {
      return;
    }

    const quoteButton = el('button.inbox-message-quote-button', {
      type: 'button',
      onclick: this.handleQuoteButtonClick.bind(this)
    }, [quoteIcon.cloneNode(true)]);

    tools.push(el('x-tooltip', { text: 'Quote' }, [quoteButton]));

  }

  tryRenderDynamicTools() {

    const tools = [...this.tools];
    const message = this.message;

    this.tryRenderQuoteTool(tools);

    if (this.isInbox === true && message.type === conversationMessageTypes.OUTBOUND) {

      let button = null;

      if (this.messagesViewer.bookmarkedMessageIds.includes(message._id) === true) {
        button = el('button.inbox-message-unbookmark-button', {
          type: 'button',
          onclick: this.handleUnbookmarkButtonClick.bind(this)
        }, [unbookmarkIcon.cloneNode(true)]);
      } else {
        button = el('button.inbox-message-bookmark-button', {
          type: 'button',
          onclick: this.handleBookmarkButtonClick.bind(this)
        }, [bookmarkIcon.cloneNode(true)]);
      }
      tools.push(button);
    }

    if (this.message.type === conversationMessageTypes.INBOUND && this.feedbacksConfiguration.canAccept(message)) {

      const feedbackButton = el('button.inbox-message-copy-button', {
        type: 'button',
        onclick: function handler() {
          this.feedbacksConfiguration.showForm(this.message);
        }.bind(this)
      }, [feedbackIcon.cloneNode(true)]);

      tools.unshift(el('x-tooltip', { text: 'Feedback' }, [feedbackButton]));

    }

    setChildren(this.toolsContainer, tools);

  }

  update(message, index, messages, messageGroup) {

    this.message = message;

    var sender = messageGroup.sender;

    if (this.messagesViewer.conversation.deliveryChannel === 'EMAIL') {
      this.showHeader(message, sender);
    } else {
      this.hideHeader();
    }

    this.profilePictureDisplay.update(message.sender, this.isInbox);

    var shouldDisplayContent = this.generationStateDisplay.update(message);

    if (shouldDisplayContent === true) {
      setAttr(this.messageContentDisplay, { style: { display: '' } });
      this.updateContent(message);
    } else {
      setAttr(this.messageContentDisplay, { style: { display: 'none' } });
    }

    this.updateMetadata(message);

    this.updateAttachments(message);

    this.updateBlockPreview(message);

    this.updateQuotedMessage(message);

    this.tryRenderDynamicTools();

    setAttr(this.el, {
      id: `message-${message._id}`
    });

  }

}

export default MessageDisplay;
